import { _2dMapQuote, _2dMapQuoteTypes } from '@larki/shared/type/order.v2';
import { Col } from 'react-bootstrap';
import WhiteContainer from '../../../components/containers/WhiteContainer';
import text, { formatCurrencyForQuote, formatSqmArea } from '../../../text';
import Item from './Item';
import React from 'react';
import {
  SITE_DEPLOYMENT_FEE_KEY,
  useEditablePrice,
  useOptionalStaffPriceEditContext,
} from '@/mp/components/Cart/StaffPriceEditContext';
import classNames from 'classnames';
import EditablePriceField from '@/mp/components/Cart/StaffPriceEditor/EditablePriceField';

const productToDisplayName: Record<_2dMapQuoteTypes['product'], string> = {
  aerial: text('aerial3D'),
  streetscape: text('streetscape3D'),
  drone: text('drone3D'),
  exterior: text('exterior3D'),
  interior: text('interior3D'),
  bim_exterior: text('bim_exterior3D'),
  bim_interior: text('bim_interior3D'),
  twod_plan_exterior: text('twod_plan_exterior3D'),
  twod_plan_interior: text('twod_plan_interior3D'),
  unknown: text('unknown3D'),
  custom: text('customMobileLabel'),
  pedestrian: text('pedestrianMobileLabel'),
  essentials_monthly: text('essentialsMembershipMonthly'),
  essentials_yearly: text('essentialsMembershipYearly'),
};

type Fee = NonNullable<_2dMapQuoteTypes['subQuote']>['totals']['fees'][number];

const FeeRow = ({
  fee,
  quote,
  isApproximate,
}: {
  fee: Fee;
  quote: _2dMapQuote;
  isApproximate?: boolean;
}) => {
  const isSiteDeploymentFee = fee.metadata.id === SITE_DEPLOYMENT_FEE_KEY;
  const edit = useEditablePrice(
    isSiteDeploymentFee ? SITE_DEPLOYMENT_FEE_KEY : undefined,
    fee.amount
  );

  return (
    <div className='d-flex flex-row justify-content-between align-items-center'>
      <p className='mb-0'>{fee.metadata.displayName}</p>
      {edit.isEditing ? (
        <EditablePriceField
          fieldKey={`rfq-edit-${SITE_DEPLOYMENT_FEE_KEY}`}
          value={edit.editableValue}
          originalValue={edit.originalValue}
          scaleFactor={quote.currency.scale_factor}
          onChange={edit.setOverride}
          hasActiveOverride={edit.hasActiveOverride}
          product={SITE_DEPLOYMENT_FEE_KEY}
        />
      ) : (
        <p className='mb-0 justify-content-end'>
          {formatCurrencyForQuote(
            fee.amount,
            quote.currency.name,
            quote.currency.scale_factor,
            { spaced: true, approximate: isApproximate }
          )}
        </p>
      )}
    </div>
  );
};

const Card = ({
  index,
  title,
  subQuote,
  quote,
  isApproximate,
}: {
  index: number;
  title: string;
  subQuote: _2dMapQuoteTypes['subQuote'];
  quote: _2dMapQuote;
  isApproximate?: boolean;
}) => {
  const editCtx = useOptionalStaffPriceEditContext();
  const isEditing = !!editCtx?.isEditing;

  return (
    <Col className='px-0 mt-2 quote-card' key={index}>
      <WhiteContainer
        className={classNames('p-3 charge-item', {
          'WhiteContainer--editing': isEditing,
        })}
      >
        <p className='title mb-2'>
          {index + 1}. {title}
        </p>
        <div className='px-3'>
          {subQuote?.charges.map((charge, index) => {
            const area =
              charge.product === 'essentials_monthly' ||
              charge.product === 'essentials_yearly'
                ? null
                : charge.quantity
                  ? formatSqmArea(charge.quantity)
                  : null;

            const product = `${productToDisplayName[charge.product]} ${area ? ` (${area})` : ''}`;

            return (
              <Item
                index={index}
                product={product}
                productKey={charge.product}
                subtotal={charge.subtotal}
                quote={quote}
                isApproximate={isApproximate}
                fullPrice={charge.baseBeforeDiscounts}
                discount={charge.appliedDiscounts.reduce(
                  (acc, discount) => acc + discount.amount,
                  0
                )}
              />
            );
          })}
          {subQuote?.totals.fees.map((fee) => (
            <FeeRow
              key={fee.metadata.id}
              fee={fee}
              quote={quote}
              isApproximate={isApproximate}
            />
          ))}
        </div>
      </WhiteContainer>
    </Col>
  );
};

export default React.memo(Card);
